import { useQuery } from '@tanstack/react-query';
import { api, type AccountTerritoryResult, type ApiError } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The territory a company is in, and how it got there (F-054, G-146).
 *
 * Every move is listed with whose it was and whose it became, because the
 * question after a rebalance is always "why is this mine now".
 */
export function AccountTerritoryPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const enabled = useEntitlement('F-054');
  const history = useQuery<AccountTerritoryResult, ApiError>({
    queryKey: ['account-territory', accountId],
    queryFn: () => api.accountTerritory({ path: { accountId } }),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled || history.isPending) return null;
  if (history.isError) {
    return (
      <Panel data-region="account-territory">
        <p className="text-dense text-danger-text">{errorText(history.error)}</p>
      </Panel>
    );
  }
  const { current, moves } = history.data;
  return (
    <Panel data-region="account-territory">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('territory.accountPanel')}</SectionLabel>
        <p data-account-territory className="text-body text-ink">
          {current ?? t('territory.noTerritory')}
        </p>
        {moves.length > 0 ? (
          <ul className="flex flex-col">
            {moves.map((move, index) => (
              <li key={`${move.at}-${String(index)}`} data-territory-move className="text-meta text-ink-meta">
                {t(`territory.reason.${move.reason}` as 'territory.reason.created', {
                  values: {
                    date: formatDate(move.at, format),
                    from: move.fromName ?? t('territory.noTerritory'),
                    to: move.toName ?? t('territory.noTerritory'),
                    owner: move.toOwner ?? move.fromOwner ?? '',
                  },
                })}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}
